import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  InputBase,
  Chip,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";

/**
 * DuplicateReviewModal - overlay panel for reviewing and resolving duplicate items/categories
 *
 * @param {Array}    itemGroups           - [{ normalizedName, ocrItem, existingItem, ocrCategoryId }]
 * @param {Array}    categoryGroups       - [{ normalizedName, ocrCategory, existingCategory }]
 * @param {string}   currencySymbol
 * @param {Function} onMergeItem          - (group, keepOcr: boolean) => void
 * @param {Function} onKeepBothItems      - (group) => void
 * @param {Function} onMergeCategory      - (group, keepOcr: boolean) => void
 * @param {Function} onKeepBothCategories - (group) => void
 * @param {Function} onUpdateOcrItem      - (group, updatedFields) => void
 * @param {Function} onClose              - () => void
 */
const DuplicateReviewModal = ({
  itemGroups = [],
  categoryGroups = [],
  currencySymbol = "$",
  onMergeItem,
  onKeepBothItems,
  onMergeCategory,
  onKeepBothCategories,
  onUpdateOcrItem,
  onClose,
}) => {
  // sub-view for "merge into" picker: null | { type, group }
  const [mergeSubView, setMergeSubView] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0); // 0 = keep OCR, 1 = keep existing

  const totalCount = itemGroups.length + categoryGroups.length;

  const openMergeInto = (type, group) => {
    setMergeSubView({ type, group });
    setSelectedIndex(0);
  };

  const handleConfirmMerge = () => {
    const keepOcr = selectedIndex === 0;
    if (mergeSubView.type === "item") {
      onMergeItem?.(mergeSubView.group, keepOcr);
    } else {
      onMergeCategory?.(mergeSubView.group, keepOcr);
    }
    setMergeSubView(null);
  };

  // ── Merge Into sub-view ───────────────────────────────────────────────────
  if (mergeSubView) {
    const { type, group } = mergeSubView;
    const ocrName = type === "item" ? group.ocrItem.name : group.ocrCategory.name;
    const existingName = type === "item" ? group.existingItem.name : group.existingCategory.name;

    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <IconButton onClick={() => setMergeSubView(null)} size="small">
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#111827", textTransform: "lowercase" }}>
            merge into
          </Typography>
        </Box>

        <Typography sx={{ fontSize: "0.78rem", color: "#6b7280", mb: 2.5, textTransform: "lowercase", lineHeight: 1.5 }}>
          choose which version to keep. the other will be removed from this import.
        </Typography>

        {/* Option 0: OCR / new */}
        <Box
          onClick={() => setSelectedIndex(0)}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            p: 1.5,
            border: selectedIndex === 0 ? "1.5px solid #7C3AED" : "1.5px solid #e5e7eb",
            borderRadius: 2,
            cursor: "pointer",
            mb: 1.5,
            bgcolor: selectedIndex === 0 ? "#fafafa" : "#fff",
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: "2px solid",
              borderColor: selectedIndex === 0 ? "#7C3AED" : "#d1d5db",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            {selectedIndex === 0 && (
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#7C3AED" }} />
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#111827", textTransform: "lowercase" }}>
                {ocrName}
              </Typography>
              <Chip label="new" size="small" sx={{ height: 16, fontSize: "0.6rem", bgcolor: "#EDE9FE", color: "#7C3AED", fontWeight: 600, "& .MuiChip-label": { px: 0.75 } }} />
            </Box>
            {type === "item" && group.ocrItem.price != null && (
              <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                {currencySymbol}{group.ocrItem.price}
              </Typography>
            )}
            {type === "category" && (
              <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>
                {group.ocrCategory.items?.length ?? 0} items
              </Typography>
            )}
          </Box>
        </Box>

        {/* Option 1: Existing */}
        <Box
          onClick={() => setSelectedIndex(1)}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            p: 1.5,
            border: selectedIndex === 1 ? "1.5px solid #7C3AED" : "1.5px solid #e5e7eb",
            borderRadius: 2,
            cursor: "pointer",
            mb: 3,
            bgcolor: selectedIndex === 1 ? "#fafafa" : "#fff",
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: "2px solid",
              borderColor: selectedIndex === 1 ? "#7C3AED" : "#d1d5db",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            {selectedIndex === 1 && (
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#7C3AED" }} />
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: "#111827", textTransform: "lowercase" }}>
                {existingName}
              </Typography>
              <Chip label="existing" size="small" sx={{ height: 16, fontSize: "0.6rem", bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 600, "& .MuiChip-label": { px: 0.75 } }} />
            </Box>
            {type === "category" && (
              <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>existing in menu</Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: "auto" }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleConfirmMerge}
            sx={{ bgcolor: "#7C3AED", textTransform: "lowercase", fontWeight: 600, borderRadius: 2, py: 1.25, "&:hover": { bgcolor: "#6200EE" } }}
          >
            confirm merge
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Main review list ──────────────────────────────────────────────────────
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose} size="small">
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827", textTransform: "lowercase" }}>
            review duplicates
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ bgcolor: "#f3f4f6", borderRadius: 2, px: 1, py: 0.25 }}>
            <Typography sx={{ fontSize: "0.72rem", color: "#6b7280", fontWeight: 600 }}>
              {totalCount} found
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
          </IconButton>
        </Box>
      </Box>

      {/* Subtitle */}
      <Typography sx={{ fontSize: "0.78rem", color: "#6b7280", textTransform: "lowercase", mb: 2, pl: 4.5 }}>
        {totalCount} item{totalCount !== 1 ? "s" : ""} need attention
      </Typography>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {/* Item groups */}
        {itemGroups.length > 0 && (
          <>
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
              items
            </Typography>
            {itemGroups.map((group) => (
              <ItemDuplicateGroup
                key={group.normalizedName}
                group={group}
                currencySymbol={currencySymbol}
                onMergeBoth={() => openMergeInto("item", group)}
                onKeepBoth={() => onKeepBothItems?.(group)}
                onUpdateOcrItem={(fields) => onUpdateOcrItem?.(group, fields)}
                onDeleteOcrItem={() => onMergeItem?.(group, false)}
              />
            ))}
          </>
        )}

        {/* Category groups */}
        {categoryGroups.length > 0 && (
          <>
            {itemGroups.length > 0 && <Divider sx={{ my: 2 }} />}
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
              categories
            </Typography>
            {categoryGroups.map((group) => (
              <CategoryDuplicateGroup
                key={group.normalizedName}
                group={group}
                onMergeBoth={() => openMergeInto("category", group)}
                onKeepBoth={() => onKeepBothCategories?.(group)}
              />
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

// ── Item duplicate group ──────────────────────────────────────────────────
const ItemDuplicateGroup = ({ group, currencySymbol, onMergeBoth, onKeepBoth, onUpdateOcrItem, onDeleteOcrItem }) => (
  <Box sx={{ mb: 2.5, border: "1px solid #e5e7eb", borderRadius: 2.5, overflow: "hidden" }}>
    {/* Group header */}
    <Box sx={{ px: 2, py: 1.5, bgcolor: "#F5F3FF" }}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827", textTransform: "lowercase" }}>
        {group.ocrItem.name}
      </Typography>
      <Typography sx={{ fontSize: "0.7rem", color: "#7C3AED", textTransform: "lowercase", mt: 0.25 }}>
        2 similar items
      </Typography>
    </Box>

    {/* Two item cards side by side */}
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid #e5e7eb" }}>
      {/* OCR / new item card */}
      <Box sx={{ borderRight: "1px solid #e5e7eb" }}>
        <ItemCard
          item={group.ocrItem}
          currencySymbol={currencySymbol}
          badge={{ label: "duplicate", bgcolor: "#FEF3C7", color: "#B45309" }}
          onUpdate={onUpdateOcrItem}
          onDelete={onDeleteOcrItem}
        />
      </Box>
      {/* Existing item card (read-only) */}
      <Box>
        <ItemCard
          item={group.existingItem}
          currencySymbol={currencySymbol}
          badge={{ label: "duplicate", bgcolor: "#FEF3C7", color: "#B45309" }}
          readOnly
        />
      </Box>
    </Box>

    {/* Actions */}
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, p: 1.25 }}>
      <Button
        size="small"
        variant="contained"
        onClick={onMergeBoth}
        sx={{
          bgcolor: "#7C3AED",
          color: "#fff",
          textTransform: "lowercase",
          fontWeight: 600,
          borderRadius: 1.5,
          fontSize: "0.78rem",
          "&:hover": { bgcolor: "#6200EE" },
        }}
      >
        merge both
      </Button>
      <Button
        size="small"
        variant="contained"
        onClick={onKeepBoth}
        sx={{
          bgcolor: "#7C3AED",
          color: "#fff",
          textTransform: "lowercase",
          fontWeight: 600,
          borderRadius: 1.5,
          fontSize: "0.78rem",
          "&:hover": { bgcolor: "#6200EE" },
        }}
      >
        keep both
      </Button>
    </Box>
  </Box>
);

// ── Category duplicate group ──────────────────────────────────────────────
const CategoryDuplicateGroup = ({ group, onMergeBoth, onKeepBoth }) => (
  <Box sx={{ mb: 2.5, border: "1px solid #e5e7eb", borderRadius: 2.5, overflow: "hidden" }}>
    {/* Group header */}
    <Box sx={{ px: 2, py: 1.5, bgcolor: "#F5F3FF" }}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827", textTransform: "lowercase" }}>
        {group.ocrCategory.name} / {group.existingCategory.name}
      </Typography>
      <Typography sx={{ fontSize: "0.7rem", color: "#7C3AED", textTransform: "lowercase", mt: 0.25 }}>
        2 similar categories
      </Typography>
    </Box>

    {/* Two category cards side by side */}
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid #e5e7eb" }}>
      {/* OCR / new */}
      <Box sx={{ p: 1.5, borderRight: "1px solid #e5e7eb" }}>
        <Chip label="new" size="small" sx={{ height: 16, fontSize: "0.58rem", bgcolor: "#EDE9FE", color: "#7C3AED", fontWeight: 600, mb: 1, "& .MuiChip-label": { px: 0.75 } }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: "#EDE9FE", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RestaurantMenuOutlinedIcon sx={{ fontSize: 16, color: "#7C3AED" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", textTransform: "lowercase" }}>
              {group.ocrCategory.name}
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "#6b7280" }}>
              {group.ocrCategory.items?.length ?? 0} items
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Existing */}
      <Box sx={{ p: 1.5 }}>
        <Chip label="existing" size="small" sx={{ height: 16, fontSize: "0.58rem", bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 600, mb: 1, "& .MuiChip-label": { px: 0.75 } }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RestaurantMenuOutlinedIcon sx={{ fontSize: 16, color: "#B45309" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827", textTransform: "lowercase" }}>
              {group.existingCategory.name}
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "#6b7280" }}>
              existing in menu
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>

    {/* Actions */}
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, p: 1.25 }}>
      <Button
        size="small"
        variant="contained"
        onClick={onMergeBoth}
        sx={{
          bgcolor: "#7C3AED",
          color: "#fff",
          textTransform: "lowercase",
          fontWeight: 600,
          borderRadius: 1.5,
          fontSize: "0.78rem",
          "&:hover": { bgcolor: "#6200EE" },
        }}
      >
        merge categories
      </Button>
      <Button
        size="small"
        variant="contained"
        onClick={onKeepBoth}
        sx={{
          bgcolor: "#7C3AED",
          color: "#fff",
          textTransform: "lowercase",
          fontWeight: 600,
          borderRadius: 1.5,
          fontSize: "0.78rem",
          "&:hover": { bgcolor: "#6200EE" },
        }}
      >
        keep both
      </Button>
    </Box>
  </Box>
);

// ── Item card (used for both OCR and existing items in a duplicate group) ──
const ItemCard = ({ item, currencySymbol, badge, onUpdate, onDelete, readOnly = false }) => {
  const [localName, setLocalName] = useState((item.name || "").toLowerCase());
  const [localDesc, setLocalDesc] = useState((item.description || "").toLowerCase());
  const [localPrice, setLocalPrice] = useState(
    item.price != null ? Number(item.price).toFixed(2) : "0.00"
  );
  const [isEditing, setIsEditing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const handleToggleEdit = () => {
    if (isEditing) {
      onUpdate?.({ name: localName, description: localDesc, price: parseFloat(localPrice) || 0 });
    }
    setIsEditing(!isEditing);
  };

  const hasImage = item.image && item.image.trim() !== "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Image area */}
      <Box
        sx={{
          width: "100%",
          height: 130,
          bgcolor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {hasImage ? (
          <Box
            component="img"
            src={item.image}
            alt={localName}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <RestaurantMenuOutlinedIcon sx={{ fontSize: 30, color: "#d1d5db" }} />
        )}
      </Box>

      {/* Details */}
      <Box sx={{ p: 1.25, flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Badge */}
        <Chip
          label={badge.label}
          size="small"
          sx={{
            height: 16,
            fontSize: "0.58rem",
            bgcolor: badge.bgcolor,
            color: badge.color,
            fontWeight: 600,
            mb: 0.75,
            alignSelf: "flex-start",
            "& .MuiChip-label": { px: 0.75 },
          }}
        />

        {/* Name */}
        {isEditing ? (
          <InputBase
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            sx={{
              fontSize: "0.82rem",
              fontWeight: 700,
              color: "#111827",
              width: "100%",
              mb: 0.5,
              borderBottom: "1px solid #7C3AED",
              "& .MuiInputBase-input": { p: 0, pb: 0.25 },
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 700,
              color: "#111827",
              textTransform: "lowercase",
              mb: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {localName}
          </Typography>
        )}

        {/* Description */}
        {localDesc ? (
          <>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "#6b7280",
                textTransform: "lowercase",
                lineHeight: 1.4,
                mb: 0.25,
                ...(showDetails
                  ? {}
                  : {
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }),
              }}
            >
              {localDesc}
            </Typography>
            <Typography
              onClick={() => setShowDetails((p) => !p)}
              sx={{
                fontSize: "0.7rem",
                color: "#7C3AED",
                fontWeight: 600,
                cursor: "pointer",
                textTransform: "lowercase",
                mb: 0.5,
              }}
            >
              {showDetails ? "hide" : "details"}
            </Typography>
          </>
        ) : (
          <Box sx={{ mb: 1 }} />
        )}

        {/* Price + edit/delete */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "auto" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              border: "1px solid #e5e7eb",
              borderRadius: 1.5,
              px: 0.75,
              py: 0.25,
            }}
          >
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#6b7280" }}>
              {currencySymbol}
            </Typography>
            <InputBase
              value={localPrice}
              onChange={(e) => setLocalPrice(e.target.value)}
              readOnly={!isEditing || readOnly}
              sx={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "#111827",
                width: 48,
                "& .MuiInputBase-input": {
                  p: 0,
                  ...(isEditing && !readOnly && { borderBottom: "1px solid #7C3AED", pb: 0.25 }),
                },
              }}
            />
          </Box>

          {!readOnly && (
            <Box sx={{ display: "flex" }}>
              <IconButton
                onClick={handleToggleEdit}
                size="small"
                sx={{ color: isEditing ? "#16a34a" : "#7C3AED", p: 0.5, "&:hover": { bgcolor: isEditing ? "#DCFCE7" : "#F3EEFF" } }}
              >
                {isEditing ? (
                  <CheckOutlinedIcon sx={{ fontSize: 16 }} />
                ) : (
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
              <IconButton
                onClick={onDelete}
                size="small"
                sx={{ color: "#DC2626", p: 0.5, "&:hover": { bgcolor: "#FEE2E2" } }}
              >
                <DeleteOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DuplicateReviewModal;
